import React, { useState, useEffect } from 'react';
import '../styles/OnboardingPopup.css';
import { 
  MdCloud, MdSecurity, MdWarning, MdRouter, MdDns, 
  MdNetworkWifi, MdStorage, MdSettings, 
  MdCode, MdApps, MdComputer, MdStorage as MdDatabase, 
  MdShield, MdTrackChanges, 
  MdLocalFireDepartment, MdAdd, MdInfo, MdCheck,
  MdLink, MdVpnKey, MdFolder, MdIntegrationInstructions,
  MdMonitor, MdThumbUp, MdEdit, MdSave
} from 'react-icons/md';
import { FaGoogleDrive, FaServer, FaDocker, FaJenkins, FaJava, FaPhp, FaNodeJs, FaSlack } from 'react-icons/fa';
import { SiKubernetes, SiApache, SiMongodb, SiMysql, SiElasticsearch, SiSplunk, SiPostgresql, SiRuby, SiJira, SiBitbucket } from 'react-icons/si';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

const OnboardingPopup = ({ isOpen, onClose }) => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  const [formData, setFormData] = useState({
    infrastructureType: 'gcp', // Default to GCP
    additionalInfo: '',
    apiLink: '',
    bucketName: '',
    accessKey: '',
    secretKey: '',
    techIntegrations: {
      docker: false,
      kubernetes: false,
      mesos: false,
      statsd: false,
      newrelic: false,
      chef: false,
      ansible: false,
      puppet: false,
      apache: false,
      splunk: false,
      vmware: false,
      jenkins: false,
      pagerduty: false,
      kafka: false,
      tomcat: false,
      elasticsearch: false,
      solr: false,
      haproxy: false,
      riak: false,
      nagios: false,
      varnish: false,
      mongodb: false,
      iis: false,
      sqlserver: false,
      cloudhealth: false,
      golang: false,
      java: false,
      php: false,
      dotnet: false,
      ruby: false,
      postgresql: false,
      cassandra: false,
      mysql: false,
      couchbase: false,
      jira: false,
      slack: false,
      bitbucket: false,
      activemq: false,
      nodejs: false,
      capistrano: false
    },
    logTypes: {
      cloudAuditLogs: false,
      vpcFlowLogs: false,
      firewallLogs: false,
      cloudDnsLogs: false,
      loadBalancerLogs: false,
      gkeLogs: false,
      cloudFunctionsLogs: false,
      cloudRunLogs: false,
      computeEngineLogs: false,
      cloudSqlLogs: false,
      cloudStorageLogs: false,
      securityCommandCenter: false,
      eventThreatDetection: false,
      cloudIdsLogs: false,
      customLogs: false
    },
    customLogSources: '',
    logSources: '' // For non-GCP infrastructure
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Load existing onboarding data when component mounts
  useEffect(() => {
    if (isOpen && user) {
      loadOnboardingData();
    }
  }, [isOpen, user]); // loadOnboardingData is stable since it doesn't use external dependencies

  // Autosave functionality - debounced save after user stops typing
  useEffect(() => {
    if (!isLoading && user && (isEditing || hasExistingData)) {
      const saveTimeout = setTimeout(() => {
        saveOnboardingData(false); // false = autosave, true = manual save
      }, 2000); // Save after 2 seconds of inactivity

      return () => clearTimeout(saveTimeout);
    }
  }, [formData, isLoading, user, isEditing, hasExistingData]); // saveOnboardingData is stable

  const loadOnboardingData = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const docRef = doc(db, 'onboarding', user.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setFormData(data.formData);
        setIsSubmitted(data.isCompleted || false);
        setHasExistingData(true);
        setLastSaved(data.lastUpdated ? data.lastUpdated.toDate() : null);
      } else {
        setHasExistingData(false);
      }
    } catch (error) {
      console.error('Error loading onboarding data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveOnboardingData = async (isManualSave = false) => {
    if (!user) return;
    
    try {
      const docRef = doc(db, 'onboarding', user.uid);
      const saveData = {
        userId: user.uid,
        userEmail: user.email,
        formData: formData,
        isCompleted: isSubmitted,
        lastUpdated: new Date(),
        completedAt: isSubmitted ? new Date() : null
      };

      if (hasExistingData) {
        await updateDoc(docRef, saveData);
      } else {
        await setDoc(docRef, {
          ...saveData,
          createdAt: new Date()
        });
        setHasExistingData(true);
      }
      
      if (isManualSave) {
        setLastSaved(new Date());
      }
    } catch (error) {
      console.error('Error saving onboarding data:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    setIsEditing(true);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      logTypes: {
        ...prevState.logTypes,
        [name]: checked
      }
    }));
    setIsEditing(true);
  };

  const handleTechIntegrationChange = (techName) => {
    setFormData(prevState => ({
      ...prevState,
      techIntegrations: {
        ...prevState.techIntegrations,
        [techName]: !prevState.techIntegrations[techName]
      }
    }));
    setIsEditing(true);
  };

  const handleFinish = async () => {
    setIsSubmitted(true);
    await saveOnboardingData(true); // Manual save with completion flag
  };

  const handleEditExisting = () => {
    setIsSubmitted(false);
    setIsEditing(true);
  };

  // Map of log types to their icons
  const logTypeIcons = {
    cloudAuditLogs: <MdSecurity className="log-icon" />,
    vpcFlowLogs: <MdRouter className="log-icon" />,
    firewallLogs: <MdLocalFireDepartment className="log-icon" />,
    cloudDnsLogs: <MdDns className="log-icon" />,
    loadBalancerLogs: <MdNetworkWifi className="log-icon" />,
    gkeLogs: <MdSettings className="log-icon" />,
    cloudFunctionsLogs: <MdCode className="log-icon" />,
    cloudRunLogs: <MdApps className="log-icon" />,
    computeEngineLogs: <MdComputer className="log-icon" />,
    cloudSqlLogs: <MdDatabase className="log-icon" />,
    cloudStorageLogs: <FaGoogleDrive className="log-icon" />,
    securityCommandCenter: <MdShield className="log-icon" />,
    eventThreatDetection: <MdWarning className="log-icon" />,
    cloudIdsLogs: <MdTrackChanges className="log-icon" />,
    customLogs: <MdAdd className="log-icon" />
  };

  // Map of technology integrations to their icons
  const techIntegrationIcons = {
    docker: <FaDocker />,
    kubernetes: <SiKubernetes />,
    mesos: <MdApps />,
    statsd: <MdInfo />,
    newrelic: <MdTrackChanges />,
    chef: <MdSettings />,
    ansible: <MdCode />,
    puppet: <MdApps />,
    apache: <SiApache />,
    splunk: <SiSplunk />,
    vmware: <MdComputer />,
    jenkins: <FaJenkins />,
    pagerduty: <MdWarning />,
    kafka: <MdStorage />,
    tomcat: <SiApache />,
    elasticsearch: <SiElasticsearch />,
    solr: <MdStorage />,
    haproxy: <MdRouter />,
    riak: <MdStorage />,
    nagios: <MdMonitor />,
    varnish: <MdNetworkWifi />,
    mongodb: <SiMongodb />,
    iis: <MdStorage />,
    sqlserver: <MdDatabase />,
    cloudhealth: <MdCloud />,
    golang: <MdCode />,
    java: <FaJava />,
    php: <FaPhp />,
    dotnet: <MdCode />,
    ruby: <SiRuby />,
    postgresql: <SiPostgresql />,
    cassandra: <MdDatabase />,
    mysql: <SiMysql />,
    couchbase: <MdDatabase />,
    jira: <SiJira />,
    slack: <FaSlack />,
    bitbucket: <SiBitbucket />,
    activemq: <MdApps />,
    nodejs: <FaNodeJs />,
    capistrano: <MdSettings />
  };

  // Infrastructure type icons
  const infrastructureIcons = {
    gcp: <MdCloud className="infra-icon" />,
    other: <FaServer className="infra-icon" />
  };

  if (!isOpen) return null;

  // Loading state
  if (isLoading) {
    return (
      <div className="onboarding-popup-overlay">
        <div className="onboarding-popup-container">
          <div className="loading-state">
            <MdSettings className="loading-icon spinning" />
            <h2>Loading your configuration...</h2>
            <p>Please wait while we retrieve your saved information.</p>
          </div>
        </div>
      </div>
    );
  }

  // Success message after submission
  if (isSubmitted) {
    return (
      <div className="onboarding-popup-overlay">
        <div className="onboarding-popup-container success-container">
          <div className="success-message">
            <MdThumbUp className="success-icon" />
            <h2>Configuration Received!</h2>
            <p>Thank you for the configuration and your input. The next step is for the system and environment to be automatically set up. This process typically completes within 48 hours.</p>
            <div className="edit-options">
              <button className="edit-button" onClick={handleEditExisting}>
                <MdEdit className="button-icon" />
                Edit Configuration
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-popup-overlay">
      <div className="onboarding-popup-container">
        <div className="popup-header">
          <MdCloud className="header-icon" />
          <h2>{hasExistingData ? 'Edit Your Configuration' : 'Welcome to SOCFirst AI'}</h2>
        </div>
        
        <p>{hasExistingData 
          ? 'You can edit your existing configuration below.' 
          : 'Please provide some information about your infrastructure to help us set up your SOCFirst AI environment:'
        }</p>
        
        <div className="form-group">
          <label htmlFor="infrastructureType">
            <MdStorage className="label-icon" />
            Infrastructure Type:
          </label>
          <div className="select-wrapper">
            <select 
              id="infrastructureType" 
              name="infrastructureType" 
              value={formData.infrastructureType}
              onChange={handleChange}
            >
              <option value="gcp">Google Cloud Platform (GCP)</option>
              <option value="other">Other</option>
            </select>
            {formData.infrastructureType && 
              <span className="infrastructure-icon">
                {infrastructureIcons[formData.infrastructureType]}
              </span>
            }
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="apiLink">
            <MdLink className="label-icon" />
            API Endpoint URL:
          </label>
          <input 
            type="text" 
            id="apiLink" 
            name="apiLink" 
            placeholder="https://api.example.com/v1"
            value={formData.apiLink}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="bucketName">
            <MdFolder className="label-icon" />
            Bucket Name:
          </label>
          <input 
            type="text" 
            id="bucketName" 
            name="bucketName" 
            placeholder="your-storage-bucket-name"
            value={formData.bucketName}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="accessKey">
            <MdVpnKey className="label-icon" />
            Access Key:
          </label>
          <input 
            type="text" 
            id="accessKey" 
            name="accessKey" 
            placeholder="Your access key"
            value={formData.accessKey}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="secretKey">
            <MdVpnKey className="label-icon" />
            Secret Key:
          </label>
          <input 
            type="password" 
            id="secretKey" 
            name="secretKey" 
            placeholder="Your secret key"
            value={formData.secretKey}
            onChange={handleChange}
          />
        </div>

        <div className="form-group tech-integrations-section">
          <label className="tech-section-label">
            <MdIntegrationInstructions className="label-icon" />
            Technology Integrations:
          </label>
          <p className="tech-section-description">Select the technologies you want to integrate with SOCFirst AI:</p>
          <div className="tech-grid">
            {Object.keys(formData.techIntegrations).map(techName => (
              <div 
                className={`tech-item ${formData.techIntegrations[techName] ? 'tech-selected' : ''}`} 
                key={techName}
                onClick={() => handleTechIntegrationChange(techName)}
              >
                <div className="tech-icon">
                  {techIntegrationIcons[techName]}
                </div>
                <div className="tech-name">
                  {techName.charAt(0).toUpperCase() + techName.slice(1)}
                </div>
                {formData.techIntegrations[techName] && 
                  <div className="tech-selected-indicator">
                    <MdCheck />
                  </div>
                }
              </div>
            ))}
          </div>
        </div>

        {formData.infrastructureType === 'gcp' && (
          <div className="form-group log-types-section">
            <label className="log-section-label">
              <MdCloud className="label-icon" />
              Available Log Types:
            </label>
            <div className="log-types-grid">
              {Object.keys(formData.logTypes).map(logType => (
                <div className="log-type-item" key={logType}>
                  <div className="checkbox-container">
                    <input 
                      type="checkbox" 
                      id={logType} 
                      name={logType} 
                      checked={formData.logTypes[logType]}
                      onChange={handleCheckboxChange}
                    />
                    <span className="custom-checkbox">
                      {formData.logTypes[logType] && <MdCheck className="check-icon" />}
                    </span>
                  </div>
                  <div className="log-type-info">
                    {logTypeIcons[logType]}
                    <label htmlFor={logType}>
                      {logType === 'cloudAuditLogs' && 'Cloud Audit Logs'}
                      {logType === 'vpcFlowLogs' && 'VPC Flow Logs'}
                      {logType === 'firewallLogs' && 'Firewall Logs'}
                      {logType === 'cloudDnsLogs' && 'Cloud DNS Logs'}
                      {logType === 'loadBalancerLogs' && 'Load Balancer Logs'}
                      {logType === 'gkeLogs' && 'GKE Logs'}
                      {logType === 'cloudFunctionsLogs' && 'Cloud Functions Logs'}
                      {logType === 'cloudRunLogs' && 'Cloud Run Logs'}
                      {logType === 'computeEngineLogs' && 'Compute Engine Logs'}
                      {logType === 'cloudSqlLogs' && 'Cloud SQL Logs'}
                      {logType === 'cloudStorageLogs' && 'Cloud Storage Logs'}
                      {logType === 'securityCommandCenter' && 'Security Command Center'}
                      {logType === 'eventThreatDetection' && 'Event Threat Detection'}
                      {logType === 'cloudIdsLogs' && 'Cloud IDS Logs'}
                      {logType === 'customLogs' && 'Custom Logs'}
                    </label>
                  </div>
                </div>
              ))}
            </div>
            
            {formData.logTypes.customLogs && (
              <div className="form-group custom-logs-input">
                <label htmlFor="customLogSources">
                  <MdInfo className="label-icon" />
                  Specify Custom Log Sources:
                </label>
                <input 
                  type="text" 
                  id="customLogSources" 
                  name="customLogSources" 
                  placeholder="e.g., Application logs, Database logs, etc."
                  value={formData.customLogSources}
                  onChange={handleChange}
                />
              </div>
            )}
          </div>
        )}

        {formData.infrastructureType && formData.infrastructureType !== 'gcp' && (
          <div className="form-group">
            <label htmlFor="logSources">
              <MdInfo className="label-icon" />
              Log Sources:
            </label>
            <input 
              type="text" 
              id="logSources" 
              name="logSources" 
              placeholder="e.g., Firewall logs, Application logs, etc."
              value={formData.logSources}
              onChange={handleChange}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="additionalInfo">
            <MdInfo className="label-icon" />
            Additional Information:
          </label>
          <textarea 
            id="additionalInfo" 
            name="additionalInfo" 
            placeholder="Any other details about your setup that would help us onboard you..."
            value={formData.additionalInfo}
            onChange={handleChange}
            rows="4"
          ></textarea>
        </div>

        <div className="popup-actions">
          <button className="finish-button" onClick={handleFinish}>
            <MdCheck className="button-icon" />
            {hasExistingData ? 'Update Configuration' : 'Finish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPopup; 